import { Box, Button, Flex, Text } from "@chakra-ui/react"
import { useRef } from "react"
import { useRecoilValue } from "recoil"
import { searchNavigationParams } from "../../../../common/constants/constants"
import { breadcrumbs, selectors } from "../../../../common/constants/search-constants"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import { useQueryParam } from "../../../../common/lib/QueryParamContext"
import { useUrlParams } from "../../../../common/lib/UrlParamsContext"
import { searchServiceLocaleState } from "../../../../common/store/settingsStore"
import { FhResponseData } from "../../../../common/types/search"
import {
  addReconstructedCategoryTreeToUrlParams,
  getIsRangeFilter,
  getRemoveAllLink,
} from "../../../../common/utils/search-utils"
import { CClose } from "../../../shared/Icons"

type Props = {
  breadcrumbFilters: FhResponseData
  breadcrumbsAttributeTypes: FhResponseData
}

const RemoveFilters = ({ breadcrumbFilters, breadcrumbsAttributeTypes }: Props) => {
  const { setUrlParams } = useUrlParams()
  const { queryParam } = useQueryParam()
  const { t } = useBrandedTranslation()
  const searchServiceLocale = useRecoilValue(searchServiceLocaleState)

  const removeFiltersContainerRef = useRef(null)

  // If on Novelties navigation page, filter out the New filter tag
  if (queryParam.name === searchNavigationParams.NEW) {
    breadcrumbFilters = breadcrumbFilters.filter(
      (crumb: FhResponseData) => crumb.name.attribute_type !== `${breadcrumbs.NEW}_${searchServiceLocale.toLowerCase()}`
    )
  }

  // If on cross selling navigation page, filter out the 'is_cross_selling_product' filter tag
  if (queryParam.name === searchNavigationParams.CROSS) {
    breadcrumbFilters = breadcrumbFilters.filter(
      (crumb: FhResponseData) =>
        crumb.name.attribute_type !== `${breadcrumbs.CROSS}_${searchServiceLocale.toLowerCase()}`
    )
  }

  const getRemoveFilterMainTitle = (attribute_type: FhResponseData) => {
    const attributeTypeMatch = breadcrumbsAttributeTypes?.attribute_type?.find(
      (attributeType: FhResponseData) => attributeType.name === attribute_type
    )

    return attributeTypeMatch?.value || ""
  }

  const handleRemoveFilter = (e: React.MouseEvent<HTMLButtonElement>, url_params: string) => {
    // Hide the clicked remove filter tag until the page is rerendered
    e.currentTarget.style.display = "none"

    // Add the category tree as a parameter to the url
    const enhancedRemoveFilterLink = addReconstructedCategoryTreeToUrlParams(
      url_params,
      queryParam.name === searchNavigationParams.SERIES
    )

    setUrlParams(enhancedRemoveFilterLink)
  }

  const handleRemoveAll: React.MouseEventHandler<HTMLButtonElement> = () => {
    // Hide the section with remove filter tags until the page is rerendered
    if (removeFiltersContainerRef?.current) removeFiltersContainerRef.current.style.display = "none"

    // Get the Remove All url from the breadcrumbs
    const removeAllFiltersUrl = getRemoveAllLink(breadcrumbFilters)

    // Add the category tree as a parameter to the url
    const enhancedRemoveAllFiltersLink = addReconstructedCategoryTreeToUrlParams(
      removeAllFiltersUrl,
      queryParam.name === searchNavigationParams.SERIES
    )

    setUrlParams(enhancedRemoveAllFiltersLink)
  }

  /* Guard clause if there are no applied filters */
  if (!breadcrumbFilters?.length) return null

  return (
    <Flex
      align="center"
      flexWrap="wrap"
      gap="4"
      mt={{ base: "6", md: "4" }}
      color="black"
      ref={removeFiltersContainerRef}
    >
      {breadcrumbFilters.map((crumb: FhResponseData) => {
        const { name, range } = crumb
        // Don't show category breadcrumbs
        if (name.attribute_type === selectors.CATEGORIES) return

        const isRangeFilter = getIsRangeFilter(crumb)
        const removeFilters = range.value_set[0].entry
        const removeFilterMainTitle = getRemoveFilterMainTitle(name.attribute_type)

        return removeFilters.map((filter: FhResponseData) => {
          const removeUrl = isRangeFilter ? crumb.link[0].url_params : filter.link[0].url_params
          const removeFilterName = isRangeFilter ? crumb.name.value : filter.value.value

          return (
            /* Remove tag for a selected filter */
            <Button
              rightIcon={<CClose />}
              iconSpacing="3"
              onClick={(e) => handleRemoveFilter(e, removeUrl)}
              h={{ base: "11", md: "10" }}
              py="3"
              pl="2"
              pr="3"
              key={filter.value.non_ml}
              _active={{ bgColor: "gray.900", color: "white" }}
              aria-label={`Remove ${removeFilterMainTitle}: ${removeFilterName} filter`}
            >
              <Box isTruncated>
                <Text as="span" fontSize="xs" fontWeight="medium" mr="1">
                  {removeFilterMainTitle}:
                </Text>
                <Text as="span" fontSize="xs" fontWeight="normal">
                  {removeFilterName}
                </Text>
              </Box>
            </Button>
          )
        })
      })}

      {/* This is the "Remove all" branded color button */}
      <Button aria-label="Remove all filters" onClick={handleRemoveAll} variant="secondary">
        {t("search-filter-reset-all-filters")}
      </Button>
    </Flex>
  )
}

export default RemoveFilters
